import { identity } from "fp";
import React from "react";
import * as ReduxReact from "react-redux";

import * as Redux from "redux";
import thunk from "redux-thunk";
import { Company } from "~/Company";
import * as Context from "~/Context";
import * as Document from "~/Document";
import * as Driver from "~/Driver";
import * as ELD from "~/ELD";
import * as Log from "~/Log";
import { MainMenu } from "~/MainMenu";
import * as Messaging from "~/Messaging";
import { Mutable } from "~/Mutable";
import { Time } from "~/Time";
import * as TimeTracking from "~/TimeTracking";
import * as WorkCode from "~/WorkCode";

import { reducer } from "./Reducer";

export type State = Time.State & {
  readonly context: Context.Context;
  readonly companies: Company.State;
  readonly drivers: Driver.State.State;
  readonly documents: Document.State.State;
  readonly elds: ELD.State.State;
  readonly logs: Log.State.State;
  readonly messages: Messaging.State.State;
  readonly timeTracking: TimeTracking.State.State;
  readonly mainMenu: MainMenu.State;
  readonly workCodes: WorkCode.State.State;
};

export namespace State {
  export type Next = Mutable<State>;

  export type Select<A> = (state: State) => A;
  export type Selector<A> = {
    readonly select: Select<A>;
    readonly equals: (a: A) => (b: A) => boolean;
  };

  export const initial = (
    company: Company,
    context: Context.Context
  ): State => ({
    context,
    companies: Company.State.initial(company),
    documents: Document.State.initial(),
    drivers: Driver.State.initial(),
    elds: ELD.State.initial(),
    logs: Log.State.initial(),
    messages: Messaging.State.initial(),
    ...Time.State.initial(context.identity.userId),
    timeTracking: TimeTracking.State.initial(),
    mainMenu: MainMenu.State.initial(),
    workCodes: WorkCode.State.initial(),
  });

  export const useSelector = function <Selection>(
    selector: Select<Selection> | Selector<Selection>
  ): Selection {
    return typeof selector === "function"
      ? ReduxReact.useSelector(selector)
      : ReduxReact.useSelector(selector.select, (a, b) =>
          selector.equals(a)(b)
        );
  };

  export const Provider = ({
    store,
    children,
  }: {
    readonly children?: Component.Children;
    readonly store: Store;
  }) => <ReduxReact.Provider store={store}>{children}</ReduxReact.Provider>;

  export type Store = ReturnType<typeof Store.create>;
  export namespace Store {
    export const create = (company: Company, context: Context.Context) =>
      Redux.createStore(
        reducer(State.initial(company, context)),
        Redux.compose(Redux.applyMiddleware(thunk), devTools())
      );

    const devTools = () =>
      "__REDUX_DEVTOOLS_EXTENSION__" in window
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).__REDUX_DEVTOOLS_EXTENSION__()
        : identity;
  }
}

export const useSelector = State.useSelector;
